import { Eye, Pin, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { searchStocks } from '../../api';
import { isWatched } from '../../alerts';
import { SearchResult } from '../../types';

// 종목 검색(이름·코드) — 디바운스 검색 + 핀/감시/열기. DayTradePage에서 분리(#82).
export default function StockSearch({ pinned, onPin, onWatch, onOpen }: {
  pinned: string[];
  onPin: (ticker: string, name: string) => void;
  onWatch: (ticker: string, name: string) => void;
  onOpen: (ticker: string, name: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try { const data = await searchStocks(query); setResults(data); setOpen(true); }
      catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
  }, [query]);

  // 🎨 규격 V2(08-23) — 시장 딱지는 뜻이 없는 분류라 중립 잉크 두 단(색 칩 남발 금지).
  const mkStyle: Record<string, string> = {
    KOSPI: 'bg-dc-bg2 text-dc-ink1 border-dc-line2',
    KOSDAQ: 'bg-dc-bg1 text-dc-ink2 border-dc-line1',
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="flex items-center gap-1.5 px-2 py-0.5 bg-dc-bg1 border border-[#202A3C] rounded-md focus-within:border-dc-point/50 transition-colors">
        <Search size={12} className={`shrink-0 ${searching ? 'text-dc-point animate-pulse' : 'text-dc-ink3'}`} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="종목 검색 (이름·코드)"
          className="flex-1 min-w-0 h-6 leading-none bg-transparent text-dc-ink1 text-[11.5px] placeholder-dc-ink3 outline-none"
        />
        {query && (
          <button onClick={() => { setQuery(''); setResults([]); setOpen(false); }} className="shrink-0 text-dc-ink3 hover:text-dc-ink2">
            <X size={12} />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div className="absolute top-full mt-1 left-0 right-0 sm:left-auto sm:w-72 bg-dc-bg1 border border-dc-line1 rounded-lg z-50 overflow-hidden">
          {results.map((item) => {
            const isPinned = pinned.includes(item.ticker);
            const watched = isWatched(item.ticker);
            return (
              <div key={item.ticker} className="flex items-center gap-2 px-2.5 py-2 hover:bg-white/[0.015] transition-colors border-b border-dc-hair/80 last:border-b-0">
                <button onClick={() => { onOpen(item.ticker, item.name); setOpen(false); }} className="flex-1 min-w-0 flex items-center gap-2 text-left">
                  <span className="text-dc-ink1 text-[13px] font-medium truncate">{item.name}</span>
                  <span className="text-dc-ink3 font-mono text-[10.5px] shrink-0">{item.ticker}</span>
                  <span className={`text-[10.5px] px-1 py-0.5 rounded-[5px] border font-medium shrink-0 ${mkStyle[item.market] ?? 'bg-dc-bg1 text-dc-ink3 border-dc-line1'}`}>{item.market}</span>
                </button>
                <button onClick={() => onPin(item.ticker, item.name)}
                  title={isPinned ? '핀 해제' : '목록 맨 위에 고정하고 계속 추적(스캔 밖 종목도 끌어옴)'}
                  className={`shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-[5px] border text-[10.5px] font-medium transition-colors ${isPinned ? 'text-dc-point border-dc-point/50 bg-dc-point/10' : 'text-dc-ink2 border-[#202A3C] hover:text-dc-point hover:border-dc-point/40'}`}>
                  <Pin size={11} className={isPinned ? 'fill-dc-point' : ''} /> {isPinned ? '핀됨' : '핀'}
                </button>
                <button onClick={() => onWatch(item.ticker, item.name)}
                  title={watched ? '감시 해제' : '이 종목의 모든 알림 계속 받기'}
                  className={`shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-[5px] border text-[10.5px] font-medium transition-colors ${watched ? 'text-dc-watch border-dc-watch/50 bg-dc-watch/10' : 'text-dc-ink2 border-[#202A3C] hover:text-dc-watch hover:border-dc-watch/40'}`}>
                  <Eye size={11} className={watched ? 'fill-dc-watch/20' : ''} /> {watched ? '감시중' : '감시'}
                </button>
              </div>
            );
          })}
          <p className="px-2.5 py-1 text-dc-ink3 text-[10.5px] bg-dc-bg0/60">📌 핀·👁 감시로 넣으면 목록에 계속 떠요 · 이름 클릭 = 차트 열기</p>
        </div>
      )}

      {open && query.trim() && !searching && results.length === 0 && (
        <div className="absolute top-full mt-1 left-0 right-0 sm:left-auto sm:w-72 bg-dc-bg1 border border-dc-line1 rounded-lg z-50 px-3 py-2.5 text-dc-ink3 text-[11.5px]">
          검색 결과 없음
        </div>
      )}
    </div>
  );
}
